/**
 * The wire shapes of the Setup-Wizard (REV-21354).
 *
 * Mirrors `ClosingSetupView` / `StartClosingDto` in `aumico-be` field for field. The step keys are
 * the contract: the import banner groups them into its rows by key, so renaming one here without
 * renaming it there drops the step from the banner.
 */

/** The launch steps, in the order the backend runs them. */
export type ClosingSetupStepKey = 'ERP_CONNECT' | 'JOURNAL_SYNC' | 'DOCUMENT_SYNC';

/** `SKIPPED` is a success — "not applicable to this closing", not a failure. */
export type ClosingSetupStepStatus = 'PENDING' | 'RUNNING' | 'DONE' | 'SKIPPED' | 'FAILED';

/** Where the accounting data comes from. */
export type ClosingAccountingSource = 'API' | 'FILE';

/** Where the documents come from. Google Drive is absent until REV-21355 ships. */
export type ClosingDocumentSource = 'SHAREPOINT' | 'MANUAL';

export interface IClosingSetupStep {
    readonly key: ClosingSetupStepKey;
    readonly status: ClosingSetupStepStatus;
    /** The server's one-line tally. Already a sentence; the view renders it verbatim. */
    readonly detail?: string;
    /**
     * `JOURNAL_SYNC` only: the years whose journal is in aumico after the step. A year that could not
     * be loaded is not listed. Absent on records from before the server reported it.
     */
    readonly loadedYears?: readonly number[];
    readonly error?: string;
    readonly startedAt?: string;
    readonly finishedAt?: string;
}

/**
 * A closing's setup as the server reports it.
 *
 * `started` is the navigation gate. It is read rather than derived from `startedAt`, so the routing
 * decision has exactly one source.
 */
export interface IClosingSetup {
    readonly projectId: string;
    readonly started: boolean;
    readonly startedAt: string | null;
    readonly finishedAt: string | null;
    readonly accountingSource: ClosingAccountingSource | null;
    readonly erpVendor: string | null;
    /**
     * REV-22007 — the uploaded trial balance a `FILE` closing imports; `null` for an API closing.
     * Optional on the wire because a backend older than the ticket does not send it.
     */
    readonly accountingFileId?: string | null;
    readonly documentSource: ClosingDocumentSource | null;
    readonly steps: readonly IClosingSetupStep[];
}

/** The wizard's answers, posted when the trustee presses "Abschlussprozess starten". */
export interface IStartClosingRequest {
    readonly accountingSource: ClosingAccountingSource;
    /**
     * Required for `API`. On `FILE` it only RECORDS the ERP the file came from (Banana, "Anderes ERP")
     * — REV-22007; the backend validates it when present and pulls nothing.
     */
    readonly erpVendor?: string;
    /** REV-22007 — the `fileId` of the uploaded trial balance. Required for `FILE`. */
    readonly accountingFileId?: string;
    /**
     * The per-org connection and the client's id on the vendor side — required for every vendor but
     * Bexio, exactly as `POST …/sync` requires them.
     */
    readonly connectionId?: string;
    readonly externalCompanyId?: string;
    /** Periods to pull as comparisons (the previous year); one already stored is never pulled again. */
    readonly comparisonPeriods?: readonly { readonly periodStart: string; readonly periodEnd: string }[];
    readonly documentSource: ClosingDocumentSource;
}

// ── REV-22007 — the accounting file (ADR-0020) ──────────────────────────────────────────────────
//
// Mirrors `AccountingFilePreview` / the 422 body of `POST …/abcp/accounting-file` in `aumico-be`.
// Every `messageKey` is a fully-qualified nested i18n key (`abcp.fileImport.*`), rendered verbatim.

/** Where the stored file stands with the virus scan. The import waits for `CLEAN`. */
export type AccountingFileScanState = 'PENDING' | 'CLEAN' | 'QUARANTINED' | 'FAILED';

/** One row the server has something to say about — `row` is the Excel row, `null` for the file. */
export interface IAccountingFileProblem {
    readonly row: number | null;
    readonly messageKey: string;
    readonly params?: Readonly<Record<string, string | number>>;
}

/** What the upload answers for a usable trial balance. Amounts in CHF. */
export interface IAccountingFilePreview {
    readonly fileId: string;
    readonly fileName: string;
    readonly accounts: number;
    readonly sumDebit: number;
    readonly sumCredit: number;
    readonly difference: number;
    /** Rows skipped with a reason (subtotals, a "Total" line) — shown, never silently dropped. */
    readonly warnings: readonly IAccountingFileProblem[];
    readonly scanStatus: AccountingFileScanState;
}

/**
 * Why a file was refused — the 422 body, or a synthesised one for 413 / 415 / a network failure so
 * the screen has ONE shape to render. Nothing was stored.
 */
export interface IAccountingFileRefusal {
    readonly messageKey: string;
    readonly params?: Readonly<Record<string, string | number>>;
    readonly rows: readonly IAccountingFileProblem[];
}
